// src/defineApp.ts
function defineApp(manifest) {
  return manifest;
}

// src/Kernel.ts
import { createWindowManager } from "@ex0/windows";

// src/commands/fs.ts
import colors from "ansi-colors";

// src/path.ts
function joinPath(...segments) {
  const joined = segments.join("/").replace(/\/+/g, "/");
  const parts = joined.split("/");
  const resolved = [];
  for (const part of parts) {
    if (part === "..") {
      resolved.pop();
    } else if (part !== "." && part !== "") {
      resolved.push(part);
    }
  }
  const result = "/" + resolved.join("/");
  return result;
}

// src/commands/fs.ts
function createFsCommands(fs) {
  const ls = {
    name: "ls",
    description: "List directory contents",
    async exec(args, ctx) {
      const target = args[0] ? joinPath(ctx.env.pwd, args[0]) : ctx.env.pwd;
      const contents = await fs.readdir(target);
      contents.sort();
      for (const file of contents) {
        const stat2 = await fs.stat(joinPath(target, file));
        if (stat2.isDirectory()) {
          ctx.io.writeln(colors.cyan(file));
        } else {
          ctx.io.writeln(file);
        }
      }
      return {};
    }
  };
  const cd = {
    name: "cd",
    description: "Change directory",
    async exec(args, ctx) {
      if (args.length === 0) {
        ctx.io.writeln("Usage: cd <path>");
        return {};
      }
      const target = joinPath(ctx.env.pwd, args[0]);
      try {
        const stat2 = await fs.stat(target);
        if (!stat2.isDirectory()) {
          ctx.io.writeln(`${args[0]} is not a directory`);
          return {};
        }
      } catch {
        ctx.io.writeln(`No such directory: ${args[0]}`);
        return {};
      }
      return { env: { pwd: target } };
    }
  };
  const mkdir = {
    name: "mkdir",
    description: "Create directory",
    async exec(args, ctx) {
      if (args.length === 0) {
        ctx.io.writeln("Usage: mkdir <name>");
        return {};
      }
      await fs.mkdir(joinPath(ctx.env.pwd, args[0]));
      return {};
    }
  };
  const pwd = {
    name: "pwd",
    description: "Print working directory",
    async exec(_args, ctx) {
      ctx.io.writeln(ctx.env.pwd);
      return {};
    }
  };
  const read = {
    name: "read",
    description: "Display file contents",
    async exec(args, ctx) {
      if (args.length === 0) {
        ctx.io.writeln("Usage: read <file>");
        return {};
      }
      try {
        const data = await fs.readFile(joinPath(ctx.env.pwd, args[0]));
        ctx.io.writeln(new TextDecoder().decode(data));
      } catch {
        ctx.io.writeln(`Cannot read: ${args[0]}`);
      }
      return {};
    }
  };
  const write = {
    name: "write",
    description: "Write to file",
    async exec(args, ctx) {
      if (args.length < 2) {
        ctx.io.writeln("Usage: write <filename> <contents...>");
        return {};
      }
      const [filename, ...rest] = args;
      await fs.writeFile(joinPath(ctx.env.pwd, filename), rest.join(" "));
      return {};
    }
  };
  const stat = {
    name: "stat",
    description: "Display file information",
    async exec(args, ctx) {
      if (args.length === 0) {
        ctx.io.writeln("Usage: stat <path>");
        return {};
      }
      try {
        const info = await fs.stat(joinPath(ctx.env.pwd, args[0]));
        ctx.io.writeln(
          JSON.stringify(
            {
              isFile: info.isFile(),
              isDirectory: info.isDirectory(),
              size: info.size
            },
            null,
            2
          )
        );
      } catch {
        ctx.io.writeln(`Cannot stat: ${args[0]}`);
      }
      return {};
    }
  };
  return [ls, cd, mkdir, pwd, read, write, stat];
}

// src/commands/help.ts
function createHelpCommand(getCommands, kernelApi) {
  return {
    name: "help",
    description: "Show available apps and commands",
    async exec(_args, ctx) {
      const apps = kernelApi.getApps();
      if (apps.length > 0) {
        ctx.io.writeln("Applications:");
        for (const app of apps) {
          const desc = app.description ? `  ${app.description}` : "";
          ctx.io.writeln(`  ${app.id.padEnd(16)}${desc}`);
        }
        ctx.io.writeln("");
      }
      const commands = getCommands();
      ctx.io.writeln("Commands:");
      for (const cmd of commands.values()) {
        ctx.io.writeln(`  ${cmd.name.padEnd(16)}${cmd.description}`);
      }
      return {};
    }
  };
}

// src/commands/kill.ts
var kill = {
  name: "kill",
  description: "Terminate a process by PID",
  async exec(args, ctx) {
    if (args.length === 0) {
      ctx.io.writeln("Usage: kill <pid>");
      return {};
    }
    const pid = parseInt(args[0], 10);
    if (isNaN(pid)) {
      ctx.io.writeln(`Invalid PID: ${args[0]}`);
      return {};
    }
    const proc = ctx.kernel.getProcesses().find((p) => p.pid === pid);
    if (proc) {
      const app = ctx.kernel.getApps().find((a) => a.id === proc.appId);
      if (app?.protected) {
        ctx.io.writeln(
          `Cannot kill protected process: ${app.name} (pid ${pid})`
        );
        return {};
      }
    }
    ctx.kernel.kill(pid);
    return {};
  }
};

// src/commands/ps.ts
var ps = {
  name: "ps",
  description: "List running processes",
  async exec(_args, ctx) {
    const procs = ctx.kernel.getProcesses();
    ctx.io.writeln("  PID  STATE     APP");
    for (const p of procs) {
      ctx.io.writeln(
        `  ${String(p.pid).padStart(4)}  ${p.state.padEnd(8)}  ${p.appId}`
      );
    }
    return {};
  }
};

// src/Kernel.ts
var Kernel = class {
  windowManager;
  fs;
  registry = /* @__PURE__ */ new Map();
  processes = /* @__PURE__ */ new Map();
  commands = /* @__PURE__ */ new Map();
  windowToPid = /* @__PURE__ */ new Map();
  nextPid = 1;
  api;
  constructor(options) {
    this.fs = options.fs;
    this.windowManager = createWindowManager({
      bounds: options.bounds
    });
    this.windowManager.on("window:focused", ({ windowId }) => {
      const pid = this.windowToPid.get(windowId);
      if (pid !== void 0) {
        const proc = this.processes.get(pid);
        proc?.handle?.onFocus?.();
      }
    });
    this.windowManager.on("window:blurred", ({ windowId }) => {
      const pid = this.windowToPid.get(windowId);
      if (pid !== void 0) {
        const proc = this.processes.get(pid);
        proc?.handle?.onBlur?.();
      }
    });
    this.windowManager.on("window:resized", ({ windowId, size }) => {
      const pid = this.windowToPid.get(windowId);
      if (pid !== void 0) {
        const proc = this.processes.get(pid);
        proc?.handle?.onResize?.(size.width, size.height);
      }
    });
    this.api = Object.freeze({
      spawn: (appId, args) => this.spawn(appId, args),
      kill: (pid) => this.kill(pid),
      exec: (cmd, io, env) => this.exec(cmd, io, env),
      getApps: () => this.getApps(),
      getProcesses: () => this.getProcesses(),
      focusProcess: (pid) => this.focusProcess(pid),
      fs: this.fs
    });
    this.registerCommand(ps);
    this.registerCommand(kill);
    this.registerCommand(createHelpCommand(() => this.commands, this.api));
    for (const cmd of createFsCommands(this.fs)) {
      this.registerCommand(cmd);
    }
  }
  install(manifest) {
    if (this.registry.has(manifest.id)) {
      throw new Error(`App already installed: ${manifest.id}`);
    }
    this.registry.set(manifest.id, manifest);
  }
  registerCommand(command) {
    this.commands.set(command.name, command);
  }
  spawn(appId, args) {
    const manifest = this.registry.get(appId);
    if (!manifest) {
      throw new Error(`Unknown app: ${appId}`);
    }
    if (manifest.singleton) {
      for (const proc of this.processes.values()) {
        if (proc.appId === appId && proc.state !== "dead") {
          this.focusProcess(proc.pid);
          return proc.pid;
        }
      }
    }
    const pid = this.nextPid++;
    if (manifest.windowless) {
      const entry2 = {
        pid,
        appId,
        state: "starting",
        windowId: null,
        startedAt: Date.now(),
        args: args ?? [],
        handle: null
      };
      this.processes.set(pid, entry2);
      return pid;
    }
    if (!manifest.window) {
      throw new Error(`App ${appId} requires a window configuration`);
    }
    const entry = {
      pid,
      appId,
      state: "starting",
      windowId: null,
      startedAt: Date.now(),
      args: args ?? [],
      handle: null
    };
    this.processes.set(pid, entry);
    const windowId = this.windowManager.open({
      title: manifest.window.title ?? manifest.name,
      size: manifest.window.size,
      constraints: manifest.window.constraints,
      data: { appId, pid }
    });
    entry.windowId = windowId;
    this.windowToPid.set(windowId, pid);
    return pid;
  }
  mountProcess(windowId, container) {
    const win = this.windowManager.getWindow(windowId);
    if (!win) {
      return;
    }
    const { pid } = win.data;
    const entry = this.processes.get(pid);
    if (!entry || entry.handle !== null || entry.state !== "starting") {
      return;
    }
    if (!this.windowToPid.has(windowId)) {
      entry.windowId = windowId;
      this.windowToPid.set(windowId, pid);
    }
    const manifest = this.registry.get(entry.appId);
    if (!manifest) {
      return;
    }
    const ctx = this.createAppContext(entry, windowId);
    try {
      const result = manifest.mount(container, ctx);
      if (result instanceof Promise) {
        result.then((handle) => {
          if (entry.state === "dead") {
            handle.dispose();
            return;
          }
          entry.handle = handle;
          entry.state = "running";
        }).catch((err) => {
          console.error(`Mount failed for ${entry.appId}:`, err);
          this.cleanupProcess(entry);
        });
      } else {
        entry.handle = result;
        entry.state = "running";
      }
    } catch (err) {
      console.error(`Mount failed for ${entry.appId}:`, err);
      this.cleanupProcess(entry);
    }
  }
  mountWindowlessProcess(pid, container) {
    const entry = this.processes.get(pid);
    if (!entry || entry.handle !== null || entry.state !== "starting") {
      return;
    }
    const manifest = this.registry.get(entry.appId);
    if (!manifest) {
      return;
    }
    const ctx = this.createWindowlessAppContext(entry);
    try {
      const result = manifest.mount(container, ctx);
      if (result instanceof Promise) {
        result.then((handle) => {
          if (entry.state === "dead") {
            handle.dispose();
            return;
          }
          entry.handle = handle;
          entry.state = "running";
        }).catch((err) => {
          console.error(`Mount failed for ${entry.appId}:`, err);
          this.cleanupProcess(entry);
        });
      } else {
        entry.handle = result;
        entry.state = "running";
      }
    } catch (err) {
      console.error(`Mount failed for ${entry.appId}:`, err);
      this.cleanupProcess(entry);
    }
  }
  requestClose(windowId) {
    const pid = this.windowToPid.get(windowId);
    if (pid !== void 0) {
      this.kill(pid);
    } else {
      this.windowManager.close(windowId);
    }
  }
  kill(pid) {
    const entry = this.processes.get(pid);
    if (!entry || entry.state === "dead") {
      return;
    }
    entry.state = "dead";
    if (entry.handle) {
      try {
        const result = entry.handle.dispose();
        if (result instanceof Promise) {
          result.catch(
            (err) => console.error(`Dispose failed for pid ${pid}:`, err)
          );
        }
      } catch (err) {
        console.error(`Dispose failed for pid ${pid}:`, err);
      }
    }
    if (entry.windowId !== null) {
      if (this.windowManager.getWindow(entry.windowId)) {
        this.windowManager.close(entry.windowId);
      }
      this.windowToPid.delete(entry.windowId);
    }
    this.processes.delete(pid);
  }
  async exec(commandStr, io, env) {
    const parts = commandStr.trim().split(/\s+/);
    const name = parts[0];
    const args = parts.slice(1);
    const command = this.commands.get(name);
    if (command) {
      const ctx = { io, kernel: this.api, env };
      return command.exec(args, ctx);
    }
    if (this.registry.has(name)) {
      this.spawn(name, args);
      return {};
    }
    io.writeln(`command not found: ${name}`);
    return {};
  }
  getApps() {
    return Array.from(this.registry.values()).map((m) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      icon: m.icon,
      singleton: m.singleton ?? false,
      windowless: m.windowless ?? false,
      protected: m.protected ?? false
    }));
  }
  getProcesses() {
    return Array.from(this.processes.values()).map((p) => ({
      pid: p.pid,
      appId: p.appId,
      state: p.state,
      windowId: p.windowId,
      startedAt: p.startedAt
    }));
  }
  focusProcess(pid) {
    const entry = this.processes.get(pid);
    if (entry && entry.windowId !== null) {
      this.windowManager.focus(entry.windowId);
    }
  }
  getKernelAPI() {
    return this.api;
  }
  createAppContext(entry, windowId) {
    const windowApi = {
      setTitle: (title) => this.windowManager.setTitle(windowId, title),
      close: () => this.requestClose(windowId),
      minimize: () => this.windowManager.minimize(windowId),
      maximize: () => this.windowManager.maximize(windowId),
      restore: () => this.windowManager.restore(windowId),
      resize: (size) => this.windowManager.resize(windowId, size)
    };
    return {
      pid: entry.pid,
      windowId,
      kernel: this.api,
      window: windowApi,
      args: entry.args
    };
  }
  createWindowlessAppContext(entry) {
    const noopWindowApi = {
      setTitle: () => {
      },
      close: () => {
      },
      minimize: () => {
      },
      maximize: () => {
      },
      restore: () => {
      },
      resize: () => {
      }
    };
    return {
      pid: entry.pid,
      windowId: null,
      kernel: this.api,
      window: noopWindowApi,
      args: entry.args
    };
  }
  cleanupProcess(entry) {
    entry.state = "dead";
    if (entry.windowId !== null) {
      if (this.windowManager.getWindow(entry.windowId)) {
        this.windowManager.close(entry.windowId);
      }
      this.windowToPid.delete(entry.windowId);
    }
    this.processes.delete(entry.pid);
  }
};
function createKernel(options) {
  return new Kernel(options);
}
export {
  Kernel,
  createKernel,
  defineApp,
  joinPath
};
